import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Music,
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Home,
  Search,
  Calendar,
  Users,
  Heart,
  Upload,
  CheckCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated, isArtist } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/discover', label: 'Discover', icon: Search },
    { href: '/playlists', label: 'Playlists', icon: Heart },
    { href: '/mentors', label: 'Mentors', icon: Users },
    { href: '/bookings', label: 'Sessions', icon: Calendar },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen text-white">
      {/* Navigation */}
      <nav className="sticky top-0 w-full z-50 bg-[#0A0B0F]/85 backdrop-blur-xl border-b border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_faith-sounds-2/artifacts/zorfcsqa_eipngKE4371%20%281%29.png"
                alt="M4manna"
                className="h-9 w-9 object-contain"
              />
              <span className="text-lg font-extrabold tracking-tight text-white">
                M4<span className="text-[#FFE500]">MANNA</span>
              </span>
            </Link>

            {/* Desktop Navigation - pill style */}
            <div className="hidden md:flex items-center gap-1 bg-[#16181D] rounded-full p-1 border border-[#1E2028]">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                      active
                        ? 'bg-[#FFE500] text-black'
                        : 'text-[#A0A0A0] hover:text-white'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Auth Buttons / User Menu */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {isArtist && (
                    <Link to="/upload" className="hidden sm:flex">
                      <button
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE500] text-black font-bold text-sm hover:bg-[#F0D800] transition-all"
                        data-testid="upload-btn"
                      >
                        <Upload className="h-4 w-4" />
                        Upload
                      </button>
                    </Link>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 p-1 pr-3 rounded-full bg-[#16181D] border border-[#1E2028] hover:border-[#FFE500]/40 transition-colors"
                        data-testid="user-menu-trigger"
                      >
                        <div className="h-8 w-8 rounded-full bg-[#FFE500] flex items-center justify-center overflow-hidden">
                          {user?.profile_image ? (
                            <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-4 w-4 text-black" />
                          )}
                        </div>
                        <span className="hidden sm:inline text-sm font-semibold">
                          {user?.full_name?.split(' ')[0]}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-[#16181D] border-[#1E2028] text-white rounded-2xl p-2">
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#1E2028] focus:text-[#FFE500]" data-testid="menu-dashboard">
                          <Music className="h-4 w-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/${user?.username}`} className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#1E2028] focus:text-[#FFE500]" data-testid="menu-profile">
                          <User className="h-4 w-4" />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      {isArtist && (
                        <DropdownMenuItem asChild>
                          <Link to="/bio-link" className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#1E2028] focus:text-[#FFE500]">
                            <CheckCircle className="h-4 w-4" />
                            Bio.Link Editor
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#1E2028] focus:text-[#FFE500]" data-testid="menu-settings">
                          <Settings className="h-4 w-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#1E2028]" />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="flex items-center gap-2 cursor-pointer rounded-lg text-red-400 focus:bg-red-500/10 focus:text-red-300"
                        data-testid="menu-logout"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <button className="px-4 py-2 rounded-full text-white hover:text-[#FFE500] text-sm font-semibold transition-colors" data-testid="login-btn">
                      Sign In
                    </button>
                  </Link>
                  <Link to="/register">
                    <button className="px-5 py-2 rounded-full bg-[#FFE500] text-black font-bold text-sm hover:bg-[#F0D800] transition-all" data-testid="register-btn">
                      Join Free
                    </button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 rounded-full bg-[#16181D] border border-[#1E2028]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0B0F]/98 border-t border-[#1E2028]">
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      active ? 'bg-[#FFE500] text-black font-semibold' : 'text-[#A0A0A0] hover:bg-[#16181D] hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="min-h-screen">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0A0B0F] border-t border-[#1E2028] py-12 mt-12">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_faith-sounds-2/artifacts/zorfcsqa_eipngKE4371%20%281%29.png"
                  alt="M4manna"
                  className="h-8 w-8"
                />
                <span className="text-lg font-extrabold">M4MANNA</span>
              </div>
              <p className="text-sm text-[#A0A0A0]">
                The sacred bridge between gospel artists and the faithful.
              </p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#FFE500] mb-4 font-bold">Explore</h4>
              <ul className="space-y-2 text-sm text-[#A0A0A0]">
                <li><Link to="/discover" className="hover:text-white transition-colors">Discover Music</Link></li>
                <li><Link to="/playlists" className="hover:text-white transition-colors">Playlists</Link></li>
                <li><Link to="/mentors" className="hover:text-white transition-colors">Mentors</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#FFE500] mb-4 font-bold">For Artists</h4>
              <ul className="space-y-2 text-sm text-[#A0A0A0]">
                <li><Link to="/register" className="hover:text-white transition-colors">Join as Artist</Link></li>
                <li><Link to="/upload" className="hover:text-white transition-colors">Upload Music</Link></li>
                <li><Link to="/verification" className="hover:text-white transition-colors">Get Verified</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#FFE500] mb-4 font-bold">Support</h4>
              <ul className="space-y-2 text-sm text-[#A0A0A0]">
                <li><a href="mailto:contact@m4manna.com" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#1E2028] mt-10 pt-8 text-center text-sm text-[#666]">
            <p>&copy; {new Date().getFullYear()} M4manna. Spreading the Gospel through Music.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
